import React, { useState } from 'react';
import { View, StyleSheet, Pressable, Image } from 'react-native';
import {
  Text,
  TextInput,
  useTheme,
  IconButton,
  Card,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Plus,
  X,
  Image as ImageIcon,
  Video,
  Star,
  Trash2,
} from 'lucide-react-native';

export function EquipmentImages() {
  const theme = useTheme();
  const colors = theme.colors;
  const isDark = theme.dark;
  const { control, watch, setValue } = useFormContext();

  const featuredImageUrl = watch('featured_image_url') || '';
  const galleryImages = watch('gallery_images') || [];
  const videoUrl = watch('video_url') || '';

  const [newImageUrl, setNewImageUrl] = useState('');

  const addGalleryImage = () => {
    if (newImageUrl.trim() && galleryImages.length < 10) {
      setValue('gallery_images', [...galleryImages, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  const removeGalleryImage = (index: number) => {
    setValue(
      'gallery_images',
      galleryImages.filter((_: any, i: number) => i !== index)
    );
  };

  const setAsFeatured = (url: string) => {
    setValue('featured_image_url', url);
  };

  return (
    <View style={styles.container}>
      {/* Featured Image */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Featured Image
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          This image will be displayed in equipment listings
        </Text>

        <Controller
          control={control}
          name='featured_image_url'
          render={({ field: { onChange, value } }) => (
            <View>
              <TextInput
                mode='outlined'
                placeholder='https://example.com/image.jpg'
                value={value}
                onChangeText={onChange}
                style={styles.input}
                outlineStyle={styles.inputOutline}
                autoCapitalize='none'
                autoCorrect={false}
                keyboardType='url'
              />

              {value && (
                <View
                  style={[
                    styles.imagePreview,
                    {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                      borderColor: isDark
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.08)',
                    },
                  ]}
                >
                  <View style={styles.featuredBadge}>
                    <Star size={12} color='#FFD700' fill='#FFD700' />
                    <Text variant='labelSmall' style={{ color: '#FFD700' }}>
                      Featured
                    </Text>
                  </View>
                  <Image
                    source={{ uri: value }}
                    style={styles.previewImage}
                    resizeMode='cover'
                  />
                  <IconButton
                    icon={() => <X size={16} color={colors.error} />}
                    onPress={() => onChange('')}
                    size={16}
                    style={styles.removeButton}
                    containerColor={
                      isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.9)'
                    }
                  />
                </View>
              )}
            </View>
          )}
        />
      </View>

      {/* Gallery Images */}
      <View style={styles.fieldContainer}>
        <View style={styles.labelRow}>
          <Text
            variant='labelLarge'
            style={[styles.label, { color: colors.onSurface }]}
          >
            Gallery Images
          </Text>
          <Text variant='labelSmall' style={{ color: colors.onSurfaceVariant }}>
            {galleryImages.length}/10
          </Text>
        </View>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Add up to 10 images to showcase different angles and features
        </Text>

        {/* Add Image Input */}
        <View style={styles.addImageRow}>
          <TextInput
            mode='outlined'
            placeholder='https://example.com/image.jpg'
            value={newImageUrl}
            onChangeText={setNewImageUrl}
            style={[styles.input, { flex: 1 }]}
            outlineStyle={styles.inputOutline}
            autoCapitalize='none'
            autoCorrect={false}
            keyboardType='url'
            onSubmitEditing={addGalleryImage}
          />
          <IconButton
            icon={() => <Plus size={20} color={colors.primary} />}
            onPress={addGalleryImage}
            mode='contained'
            containerColor={colors.primaryContainer}
            size={20}
            disabled={galleryImages.length >= 10 || !newImageUrl.trim()}
          />
        </View>

        {/* Gallery Grid */}
        {galleryImages.length > 0 && (
          <View style={styles.galleryGrid}>
            {galleryImages.map((url: string, index: number) => (
              <View
                key={index}
                style={[
                  styles.galleryItem,
                  {
                    backgroundColor: isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                    borderColor: isDark
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.08)',
                  },
                ]}
              >
                <Image
                  source={{ uri: url }}
                  style={styles.galleryImage}
                  resizeMode='cover'
                />
                <View style={styles.galleryActions}>
                  {url !== featuredImageUrl && (
                    <Pressable
                      onPress={() => setAsFeatured(url)}
                      style={[
                        styles.actionChip,
                        {
                          backgroundColor: isDark
                            ? 'rgba(255, 215, 0, 0.15)'
                            : 'rgba(255, 215, 0, 0.2)',
                        },
                      ]}
                    >
                      <Star size={10} color='#FFD700' />
                      <Text
                        variant='labelSmall'
                        style={{ color: '#FFD700', fontSize: 9 }}
                      >
                        Set Featured
                      </Text>
                    </Pressable>
                  )}
                  <IconButton
                    icon={() => <Trash2 size={14} color={colors.error} />}
                    onPress={() => removeGalleryImage(index)}
                    size={14}
                    containerColor={
                      isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.9)'
                    }
                  />
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Empty State */}
        {galleryImages.length === 0 && (
          <View
            style={[
              styles.emptyState,
              {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.03)'
                  : 'rgba(0, 0, 0, 0.01)',
                borderColor: isDark
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.06)',
              },
            ]}
          >
            <ImageIcon size={40} color={colors.onSurfaceVariant} />
            <Text
              variant='titleSmall'
              style={{
                color: colors.onSurface,
                marginTop: 12,
                marginBottom: 4,
              }}
            >
              No gallery images
            </Text>
            <Text
              variant='bodySmall'
              style={{ color: colors.onSurfaceVariant, textAlign: 'center' }}
            >
              Add images to showcase your equipment
            </Text>
          </View>
        )}
      </View>

      {/* Video URL */}
      <View style={styles.fieldContainer}>
        <Text
          variant='labelLarge'
          style={[styles.label, { color: colors.onSurface }]}
        >
          Video URL (Optional)
        </Text>
        <Text
          variant='bodySmall'
          style={[styles.helperText, { color: colors.onSurfaceVariant }]}
        >
          Add a YouTube or Vimeo video URL to showcase the equipment in action
        </Text>

        <Controller
          control={control}
          name='video_url'
          render={({ field: { onChange, value } }) => (
            <View>
              <TextInput
                mode='outlined'
                placeholder='https://youtube.com/watch?v=...'
                value={value}
                onChangeText={onChange}
                style={styles.input}
                outlineStyle={styles.inputOutline}
                autoCapitalize='none'
                autoCorrect={false}
                keyboardType='url'
                left={
                  <TextInput.Icon
                    icon={() => (
                      <Video size={20} color={colors.onSurfaceVariant} />
                    )}
                  />
                }
              />

              {value && (
                <View
                  style={[
                    styles.videoPreview,
                    {
                      backgroundColor: isDark
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.02)',
                      borderColor: isDark
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.08)',
                    },
                  ]}
                >
                  <Video size={24} color={colors.primary} />
                  <Text
                    variant='bodySmall'
                    style={{ color: colors.onSurface, flex: 1 }}
                    numberOfLines={1}
                  >
                    {value}
                  </Text>
                  <IconButton
                    icon={() => <X size={16} color={colors.error} />}
                    onPress={() => onChange('')}
                    size={16}
                  />
                </View>
              )}
            </View>
          )}
        />
      </View>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Media
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Use high-quality images (min 800x800px){'\n'}• Maximum 10 gallery
            images{'\n'}• Featured image appears in listings{'\n'}• Images
            should show the equipment clearly{'\n'}• Add videos to showcase
            equipment in action
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 24,
  },
  fieldContainer: {
    gap: 8,
  },
  label: {
    fontWeight: '600',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  helperText: {
    fontSize: 12,
    marginTop: -4,
  },
  input: {
    fontSize: 15,
  },
  inputOutline: {
    borderRadius: 12,
  },
  imagePreview: {
    marginTop: 12,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  featuredBadge: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
    zIndex: 1,
  },
  previewImage: {
    width: '100%',
    height: 200,
  },
  removeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  addImageRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  galleryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 12,
  },
  galleryItem: {
    width: '48%',
    aspectRatio: 1,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  galleryImage: {
    width: '100%',
    height: '100%',
  },
  galleryActions: {
    position: 'absolute',
    top: 8,
    right: 8,
    gap: 4,
  },
  actionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 16,
    borderWidth: 2,
    borderStyle: 'dashed',
    marginTop: 12,
  },
  videoPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 12,
  },
  card: {
    marginTop: 8,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
});
